import type { MemberStanding, OwnerAccess, VirtualAccountRole } from '@/api/memberStanding';
import { useMe } from './meHooks';

/** What a page may offer on one virtual account, from the caller's served standing (TMMAPP-0110). */
export interface VirtualAccountRoleView {
  role: VirtualAccountRole;
  isOwner: boolean;
  /** A Manager: every writer path except the owner-only ones (sharing, archive, envelope move, AI). */
  isManager: boolean;
  /** A Viewer (MM-D-115): reads, changes nothing. */
  isViewer: boolean;
  /**
   * The writer paths are open (the served `canManage`): the owner or a Manager, on an ACTIVE virtual
   * account whose owner is ACTIVE (MM-D-116). False for everyone while the owner is in GRACE.
   */
  canWrite: boolean;
  ownerAccess: OwnerAccess;
  /** The owner's grace end (a civil date), while `ownerAccess` is GRACE. */
  readOnlyUntil: string | null;
}

/** The view of a served standing; `null` (no standing yet) reads as a Viewer who can write nothing. */
export function roleView(standing: MemberStanding | null): VirtualAccountRoleView {
  const role = standing?.myRole ?? 'VIEWER';
  const isOwner = standing?.isOwner ?? false;
  return {
    role,
    isOwner,
    isManager: !isOwner && role === 'MANAGER',
    isViewer: !isOwner && role === 'VIEWER',
    canWrite: standing?.canManage ?? false,
    ownerAccess: standing?.ownerAccess ?? 'ACTIVE',
    readOnlyUntil: standing?.readOnlyUntil ?? null,
  };
}

/**
 * The one role hook (TMMAPP-0110): the caller's standing on a virtual account, from the page's own
 * read of it (the 0146 amendment: "from each virtual account read"), and from `/me` until that read
 * lands (so a LOCKED owner's virtual account skips the read). Never inferred from the share list.
 */
export function useVirtualAccountRole(
  virtualAccountId: number,
  detail?: MemberStanding | null,
): VirtualAccountRoleView {
  const me = useMe();
  const listed = me.virtualAccounts.find((entry) => entry.id === virtualAccountId) ?? null;
  return roleView(detail ?? listed ?? null);
}
